import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { ApplicantRiskDetail } from '@mobi/api-types/index'
import {
  TypeSignUpApplicationRequest,
  TypeSignUpApplicationResponse,
} from '../Hooks/useSignUpApplication'

type SignUpState = {
  signUp: State
}

export type TypeVerifiedFrom = 'verified-address' | 'verified-identification' | 'n/a'

export type State = {
  signupApplication: TypeSignUpApplicationRequest
  signupVerification: TypeSignUpApplicationResponse
  signupRiskAssessmentAnswers: {
    employmentStatus: string
    occupation: string
    industryParticipant: string
    bettingCustomerType: string
  }
  signupRiskAssessmentAnswerLookupKeys?: ApplicantRiskDetail
  numberOfAttemptsToVerify: number //for ga only
  isNewUserAutoLoggedIn: boolean
  hasUserStartedSignup: boolean //for ga only
  mobileNumberVerified: boolean
  isVerificationAccount: boolean
  showPreviousAddress: boolean
  isUserKnownByOtherNames: boolean
  isApplicationPendingReview: boolean
  isWageringAccountPendingCreation: boolean
  isManualAddressEntry: boolean // for ga only
}

export const initialState: State = {
  numberOfAttemptsToVerify: 0,
  isNewUserAutoLoggedIn: false,
  hasUserStartedSignup: false,
  mobileNumberVerified: false,
  isVerificationAccount: false,
  showPreviousAddress: false,
  isUserKnownByOtherNames: false,
  isApplicationPendingReview: false,
  isWageringAccountPendingCreation: false,
  signupApplication: {
    firstName: '',
    middleName: '',
    surname: '',
    firstNameAlias: '',
    lastNameAlias: '',
    birthdate: '',
    email: '',
    mobileNumber: '',
    marketingOptInIndicator: false,
    applicationReferenceNumber: 0,
    enquiryId: '',
  },
  signupVerification: {
    applicationReferenceNumber: 0,
    duplicateExists: false,
    isDuplicateClosed: false,
    isVerified: false,
    unVerifiedReason: '',
  },
  signupRiskAssessmentAnswers: {
    employmentStatus: '',
    occupation: '',
    industryParticipant: '',
    bettingCustomerType: '',
  },
  isManualAddressEntry: false,
}

const slice = createSlice({
  name: 'sign-up',
  initialState: initialState,
  reducers: {
    setSignupApplication(state, action: PayloadAction<TypeSignUpApplicationRequest>) {
      state.signupApplication = action.payload
    },
    setSignupVerification(state, action: PayloadAction<TypeSignUpApplicationResponse>) {
      state.signupVerification = action.payload
    },
    setNumberOfAttemptsToVerify(state) {
      state.numberOfAttemptsToVerify += 1
    },
    setIsNewUserAutoLoggedIn(state, action: PayloadAction<boolean>) {
      state.isNewUserAutoLoggedIn = action.payload
    },
    setSignupNewSession() {
      return initialState
    },
    setHasUserStartedSignup(state, action: PayloadAction<boolean>) {
      state.hasUserStartedSignup = action.payload
    },
    setMobileNumberVerified(state) {
      state.mobileNumberVerified = true
    },
    setIsVerificationAccount(state, action: PayloadAction<boolean>) {
      state.isVerificationAccount = action.payload
    },
    setShowPreviousAddress(state, action: PayloadAction<boolean>) {
      state.showPreviousAddress = action.payload
    },
    setUserKnownByOtherNames(state, action: PayloadAction<boolean>) {
      state.isUserKnownByOtherNames = action.payload
    },
    setIsApplicationPendingReview(state, action: PayloadAction<boolean>) {
      state.isApplicationPendingReview = action.payload
    },
    setIsWageringAccountPendingCreation(state, action: PayloadAction<boolean>) {
      state.isWageringAccountPendingCreation = action.payload
    },
    setSignupRiskAssessmentAnswers(
      state,
      action: PayloadAction<{
        employmentStatus: string
        occupation: string
        industryParticipant: string
        bettingCustomerType: string
      }>
    ) {
      state.signupRiskAssessmentAnswers = action.payload
    },
    setSignupRiskAssessmentAnswerLookupKeys(state, action: PayloadAction<ApplicantRiskDetail>) {
      state.signupRiskAssessmentAnswerLookupKeys = action.payload
    },
    setIsManualAddressEntry(state, action: PayloadAction<boolean>) {
      state.isManualAddressEntry = action.payload
    },
  },
})

export const {
  setSignupApplication,
  setSignupVerification,
  setNumberOfAttemptsToVerify,
  setIsNewUserAutoLoggedIn,
  setSignupNewSession,
  setHasUserStartedSignup,
  setMobileNumberVerified,
  setIsVerificationAccount,
  setUserKnownByOtherNames,
  setShowPreviousAddress,
  setIsApplicationPendingReview,
  setIsWageringAccountPendingCreation,
  setSignupRiskAssessmentAnswers,
  setSignupRiskAssessmentAnswerLookupKeys,
  setIsManualAddressEntry,
} = slice.actions

export default slice.reducer

export function getSignUpState(state: SignUpState): State {
  return state.signUp
}
